import { Store, set, get, del, clear, IDBIsClosingError } from './idb-keyval';

const readRequestTimeout = 100;
// We set a longer write timeout because if you await the write promise
// you might depend on the value getting written.
const writeRequestTimeout = 1000;
// When clearing we give an even higher timeout since you might really
// want to only continue after it's been cleared.
const clearRequestTimeout = 3000;

export default (dbName: string) => {
  if (!self.indexedDB) {
    return createDummyStory();
  }

  let store: Store;
  try {
    store = new Store(dbName, dbName);
  } catch (e) {
    logError(e);
    return createDummyStory();
  }

  return {
    getItem<T>(key: string): Promise<T | null> {
      return Promise.race<any>([timeout(readRequestTimeout), init(dbName, store).then(() => get<T>(key, store))]).then(
        emptyIfTimeout,
        emptyIfErrorBecauseIDBIsClosing,
      );
    },
    setItem<T>(key: string, value: T) {
      return Promise.race<any>([
        timeout(writeRequestTimeout),
        init(dbName, store).then(() => set(key, value, store)),
      ]).then(emptyIfTimeout, emptyIfErrorBecauseIDBIsClosing);
    },
    removeItem(key: string) {
      return Promise.race<any>([timeout(writeRequestTimeout), init(dbName, store).then(() => del(key, store))]).then(
        emptyIfTimeout,
        emptyIfErrorBecauseIDBIsClosing,
      );
    },
    clear() {
      return Promise.race<any>([
        timeout(clearRequestTimeout),
        init(dbName, store).then(() => clearAndSetVersion(store)),
      ]).then(emptyIfTimeout, emptyIfErrorBecauseIDBIsClosing);
    },
  };
};

function logError(...e: any[]) {
  if (self.console && self.console.error) {
    // tslint:disable:no-string-literal
    self.console['error'](e);
    // tslint:enable:no-string-literal
  }
}

const createDummyStory = () => {
  return {
    getItem<T>(key: string): Promise<T> {
      return (Promise.resolve(null) as any) as Promise<T>;
    },
    setItem<T>(key: string, value: T) {
      return Promise.resolve(null);
    },
    removeItem(key: string) {
      return Promise.resolve(null);
    },
    clear() {
      return Promise.resolve(null);
    },
  };
};

const versionKey = 'local-db-version';
async function clearAndSetVersion(store: Store) {
  await clear(store);
  await set(versionKey, CURRENT_VERSION, store);
}

const initPromises: { [dbName: string]: Promise<any> } = {};
function init(dbName: string, store: Store) {
  if (!initPromises[dbName]) {
    initPromises[dbName] = get<string>(versionKey, store).then(v => {
      if (!v) {
        return clearAndSetVersion(store);
      }
      let version = v;
      let currentVersion = CURRENT_VERSION;

      if(TREAT_EVERY_RELEASE_AS_MAJOR) {
        if (version !== currentVersion) {
          return clearAndSetVersion(store);
        }
      } 
      else {
        const dbVersionParts = v.split('.');
        const currentVersionParts = CURRENT_VERSION.split('.');
        
        if (dbVersionParts[0] !== currentVersionParts[0]) {
          return clearAndSetVersion(store);
        }
      }
    });
  }
  return initPromises[dbName];
}

function emptyIfErrorBecauseIDBIsClosing(err: any) {
  if (err === IDBIsClosingError) {
    // See comment in _withIDBStore() in idb-keyval.ts
    logError('A handled, unexpected IndexedDB error occured: ', err.err);
    return Promise.resolve(null);
  } else {
    return Promise.reject(err);
  }
}

function emptyIfTimeout(item: any) {
  if (item && item.timeout === true) {
    if (console && console.debug) {
      console.debug('Timing out local db request because it took more than ' + readRequestTimeout + 'ms');
    }
    return null;
  }
  return item;
}

function timeout(timeout: number) {
  return new Promise(resolve => {
    setTimeout(() => {
      resolve({ timeout: true });
    }, timeout);
  });
}
